(() => {
  "use strict";

  const instances = new WeakMap();
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  const addListener = (target, type, listener, options, cleanup) => {
    if (!target) return;
    target.addEventListener(type, listener, options);
    cleanup.push(() => target.removeEventListener(type, listener, options));
  };

  const setControlState = (button, disabled) => {
    if (!button) return;
    button.disabled = disabled;
    button.tabIndex = disabled ? -1 : 0;
    button.setAttribute("aria-disabled", String(disabled));
  };

  const productCardAddTimeout = 12_000;
  const productCardCartTimeout = 3_500;
  const productCardAmbiguousMessage = "We couldn’t confirm whether this item was added. Check your bag before trying again.";
  const productCardInventoryFailure = /\b(?:sold out|out of stock|insufficient inventory|not enough (?:items? )?in stock|inventory (?:is )?(?:unavailable|depleted)|requested quantity (?:is )?(?:not available|unavailable))\b/i;

  const ensureCartCoordinator = () => {
    const existing = window.__jscaCartCoordinator;
    const coordinator = existing && typeof existing === "object" ? existing : {};
    if (!Number.isSafeInteger(coordinator.refreshSequence) || coordinator.refreshSequence < 0) coordinator.refreshSequence = 0;
    if (!Number.isSafeInteger(coordinator.latestRefreshToken) || coordinator.latestRefreshToken < 0) {
      coordinator.latestRefreshToken = coordinator.refreshSequence;
    }
    coordinator.refreshSequence = Math.max(coordinator.refreshSequence, coordinator.latestRefreshToken);
    if (typeof coordinator.reserveRefresh !== "function") {
      coordinator.reserveRefresh = () => {
        coordinator.refreshSequence = coordinator.refreshSequence < Number.MAX_SAFE_INTEGER
          ? coordinator.refreshSequence + 1
          : 1;
        coordinator.latestRefreshToken = coordinator.refreshSequence;
        return coordinator.latestRefreshToken;
      };
    }
    if (typeof coordinator.isLatestRefresh !== "function") {
      coordinator.isLatestRefresh = (token) => token === coordinator.latestRefreshToken;
    }
    window.__jscaCartCoordinator = coordinator;
    return coordinator;
  };

  const internalPath = (value) => {
    if (!value) return "";
    try {
      const url = new URL(value, window.location.href);
      return url.origin === window.location.origin ? `${url.pathname}${url.search}` : "";
    } catch (_) {
      return "";
    }
  };

  const isRecord = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

  const idLike = (value) => {
    if (Number.isInteger(value) && value > 0) return true;
    return typeof value === "string" && value.length <= 120 && /\d/.test(value) && /^[A-Za-z0-9:/_-]+$/.test(value);
  };

  const validCartAddPayload = (payload) => {
    if (!isRecord(payload)) return false;
    if (idLike(payload.id ?? payload.variant_id)) return true;
    return Array.isArray(payload.items)
      && payload.items.length > 0
      && payload.items.every((item) => isRecord(item) && idLike(item.id ?? item.variant_id));
  };

  const safeShopifyMessage = (payload) => {
    if (!isRecord(payload)) return "";
    const values = [payload.description, payload.message, payload.error];
    if (typeof payload.errors === "string") values.push(payload.errors);
    else if (isRecord(payload.errors)) values.push(...Object.values(payload.errors));
    const candidate = values.flatMap((value) => Array.isArray(value) ? value : [value])
      .find((value) => typeof value === "string");
    if (!candidate) return "";
    const message = candidate.replace(/\s+/g, " ").trim();
    return message && message.length <= 240 && !/[<>]/.test(message) ? message : "";
  };

  const parseJsonResponse = async (response) => {
    const text = await response.text();
    if (!text.trim()) return { parsed: false, payload: null };
    try {
      return { parsed: true, payload: JSON.parse(text) };
    } catch (_) {
      return { parsed: false, payload: null };
    }
  };

  const withTimeout = async (task, controller, timeoutMs, code) => {
    let timedOut = false;
    let timeoutId = 0;
    const timeout = new Promise((_, reject) => {
      timeoutId = window.setTimeout(() => {
        timedOut = true;
        controller.abort();
        reject(Object.assign(new Error(code), { code }));
      }, timeoutMs);
    });
    try {
      return await Promise.race([Promise.resolve().then(task), timeout]);
    } catch (error) {
      if (timedOut) throw Object.assign(new Error(code), { code });
      throw error;
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const enablePointerDrag = (rail, cleanup) => {
    let pointerId = null;
    let startX = 0;
    let startScrollLeft = 0;
    let dragged = false;
    let suppressClick = false;

    const finish = (event) => {
      if (pointerId === null || (event.pointerId !== undefined && event.pointerId !== pointerId)) return;
      if (rail.hasPointerCapture?.(pointerId)) rail.releasePointerCapture(pointerId);
      suppressClick = dragged;
      pointerId = null;
      dragged = false;
      rail.classList.remove("is-dragging");
    };

    addListener(rail, "dragstart", (event) => {
      if (event.target instanceof HTMLImageElement) event.preventDefault();
    }, undefined, cleanup);

    addListener(rail, "pointerdown", (event) => {
      if (event.pointerType !== "mouse" || event.button !== 0) return;
      if (event.target.closest?.("[data-card-media-control], [data-card-size-tray], .homepage-product-card__quick-view")) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startScrollLeft = rail.scrollLeft;
      dragged = false;
    }, undefined, cleanup);

    addListener(rail, "pointermove", (event) => {
      if (event.pointerId !== pointerId) return;
      const distance = event.clientX - startX;
      if (!dragged && Math.abs(distance) < 6) return;
      dragged = true;
      rail.setPointerCapture?.(pointerId);
      rail.classList.add("is-dragging");
      rail.scrollLeft = startScrollLeft - distance;
      event.preventDefault();
    }, undefined, cleanup);

    addListener(rail, "pointerup", finish, undefined, cleanup);
    addListener(rail, "pointercancel", finish, undefined, cleanup);
    addListener(rail, "click", (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      suppressClick = false;
    }, true, cleanup);
  };

  const createRailController = (root, rail, { centered = false, onIndexChange } = {}, cleanup) => {
    const previous = root.querySelector("[data-rail-previous]");
    const next = root.querySelector("[data-rail-next]");
    let activeIndex = 0;
    let frame = 0;
    let userNavigation = false;
    let userNavigationTimer = 0;

    const items = () => Array.from(rail.querySelectorAll(":scope > [data-rail-item]"));
    const maximumScroll = () => Math.max(0, rail.scrollWidth - rail.clientWidth);
    const overflows = () => maximumScroll() > 2;
    const markUserNavigation = () => {
      userNavigation = true;
      window.clearTimeout(userNavigationTimer);
      userNavigationTimer = window.setTimeout(() => { userNavigation = false; }, 200);
    };

    const nearestIndex = () => {
      const currentItems = items();
      if (!currentItems.length) return 0;
      const target = centered ? rail.scrollLeft + rail.clientWidth / 2 : rail.scrollLeft;
      let nearest = 0;
      let distance = Infinity;

      currentItems.forEach((item, index) => {
        const point = centered ? item.offsetLeft + item.offsetWidth / 2 : item.offsetLeft;
        const nextDistance = Math.abs(point - target);
        if (nextDistance < distance) {
          distance = nextDistance;
          nearest = index;
        }
      });
      return nearest;
    };

    const update = () => {
      frame = 0;
      const canScroll = overflows();
      rail.tabIndex = canScroll ? 0 : -1;
      const previousDisabled = !canScroll || rail.scrollLeft <= 2;
      const nextDisabled = !canScroll || rail.scrollLeft >= maximumScroll() - 2;
      const focusedControl = document.activeElement;
      if (focusedControl === previous && previousDisabled) {
        setControlState(next, nextDisabled);
        (nextDisabled || !next ? rail : next).focus({ preventScroll: true });
      } else if (focusedControl === next && nextDisabled) {
        setControlState(previous, previousDisabled);
        (previousDisabled || !previous ? rail : previous).focus({ preventScroll: true });
      }
      setControlState(previous, previousDisabled);
      setControlState(next, nextDisabled);
      const nextIndex = nearestIndex();
      if (nextIndex !== activeIndex) {
        activeIndex = nextIndex;
        onIndexChange?.(activeIndex, userNavigation);
      }
    };

    const scheduleUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    const scrollToIndex = (index, initiatedByUser = false) => {
      const currentItems = items();
      if (!currentItems.length) return;
      if (initiatedByUser) markUserNavigation();
      const boundedIndex = Math.max(0, Math.min(index, currentItems.length - 1));
      const item = currentItems[boundedIndex];
      const left = centered
        ? item.offsetLeft - (rail.clientWidth - item.offsetWidth) / 2
        : item.offsetLeft;
      rail.scrollTo({ left, behavior: reducedMotion.matches ? "auto" : "smooth" });
      activeIndex = boundedIndex;
      onIndexChange?.(activeIndex, initiatedByUser);
      window.requestAnimationFrame(scheduleUpdate);
    };

    addListener(rail, "pointerdown", markUserNavigation, { passive: true }, cleanup);
    addListener(rail, "pointermove", (event) => {
      if (event.buttons === 1) markUserNavigation();
    }, { passive: true }, cleanup);
    addListener(rail, "touchstart", markUserNavigation, { passive: true }, cleanup);
    addListener(rail, "wheel", markUserNavigation, { passive: true }, cleanup);
    addListener(rail, "scroll", () => {
      if (userNavigation) markUserNavigation();
      scheduleUpdate();
    }, { passive: true }, cleanup);
    addListener(rail, "keydown", (event) => {
      if (event.target !== rail) return;
      const currentItems = items();
      if (!currentItems.length) return;
      if (event.key === "ArrowLeft") scrollToIndex(nearestIndex() - 1, true);
      else if (event.key === "ArrowRight") scrollToIndex(nearestIndex() + 1, true);
      else if (event.key === "Home") scrollToIndex(0, true);
      else if (event.key === "End") scrollToIndex(currentItems.length - 1, true);
      else return;
      event.preventDefault();
    }, undefined, cleanup);

    addListener(previous, "click", () => scrollToIndex(nearestIndex() - 1, true), undefined, cleanup);
    addListener(next, "click", () => scrollToIndex(nearestIndex() + 1, true), undefined, cleanup);
    enablePointerDrag(rail, cleanup);

    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(rail);
    cleanup.push(() => resizeObserver.disconnect());

    const mutationObserver = new MutationObserver(scheduleUpdate);
    mutationObserver.observe(rail, { childList: true });
    cleanup.push(() => mutationObserver.disconnect());

    update();
    onIndexChange?.(activeIndex);
    cleanup.push(() => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(userNavigationTimer);
    });
    return { scrollToIndex, update, nearestIndex };
  };

  const initEditorial = (root, cleanup) => {
    const compactMedia = window.matchMedia("(max-width: 768px)");

    root.querySelectorAll(".homepage-editorial__media img").forEach((image) => {
      addListener(image, "error", () => { image.hidden = true; }, undefined, cleanup);
    });

    root.querySelectorAll("[data-video-tile]").forEach((tile) => {
      const videos = Array.from(tile.querySelectorAll("[data-background-video]"));
      const button = tile.querySelector("[data-video-toggle]");
      const icon = button?.querySelector("[data-video-icon]");
      const label = button?.querySelector("[data-video-label]");
      let userPaused = reducedMotion.matches;

      const currentVideo = () => {
        const preferredClass = compactMedia.matches
          ? "homepage-editorial__video--mobile"
          : "homepage-editorial__video--desktop";
        return videos.find((video) => video.classList.contains(preferredClass) && !video.hidden) || null;
      };

      const updateButton = () => {
        const video = currentVideo();
        if (!button) return;
        button.hidden = !video;
        if (!video) return;
        const paused = video.paused;
        const text = paused ? "Play background video" : "Pause background video";
        button.setAttribute("aria-label", text);
        if (label) label.textContent = text;
        if (icon) icon.textContent = paused ? "▶" : "II";
      };

      const synchronize = () => {
        const active = currentVideo();
        videos.forEach((video) => {
          if (video !== active) video.pause();
        });
        if (!active) {
          tile.classList.add("is-media-error");
          updateButton();
          return;
        }
        tile.classList.remove("is-media-error");
        if (document.hidden || userPaused || reducedMotion.matches) {
          active.pause();
          updateButton();
          return;
        }
        active.play().catch(updateButton);
        updateButton();
      };

      videos.forEach((video) => {
        addListener(video, "error", () => {
          video.hidden = true;
          synchronize();
        }, undefined, cleanup);
        addListener(video, "play", updateButton, undefined, cleanup);
        addListener(video, "pause", updateButton, undefined, cleanup);
      });

      addListener(button, "click", () => {
        const active = currentVideo();
        if (!active) return;
        if (active.paused) {
          userPaused = false;
          active.play().catch(updateButton);
        } else {
          userPaused = true;
          active.pause();
        }
        updateButton();
      }, undefined, cleanup);

      const breakpointChange = synchronize;
      compactMedia.addEventListener("change", breakpointChange);
      cleanup.push(() => compactMedia.removeEventListener("change", breakpointChange));

      const motionChange = () => {
        if (reducedMotion.matches) userPaused = true;
        synchronize();
      };
      reducedMotion.addEventListener("change", motionChange);
      cleanup.push(() => reducedMotion.removeEventListener("change", motionChange));

      addListener(document, "visibilitychange", synchronize, undefined, cleanup);
      synchronize();
      cleanup.push(() => videos.forEach((video) => video.pause()));
    });
  };

  const initProductCarousel = (root, cleanup) => {
    const rail = root.querySelector("[data-product-rail]");
    const status = root.querySelector("[data-carousel-status]");
    const cards = Array.from(root.querySelectorAll(".homepage-product-card"));
    const cardStates = new WeakMap();
    const cartCoordinator = ensureCartCoordinator();
    const collectionHeroImage = root.querySelector(".jsca-collection__hero-image");
    let reportedIndex = 0;

    const beamObserver = typeof IntersectionObserver === "function"
      ? new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting, intersectionRatio }) => {
          target.classList.toggle("is-card-beam-visible", isIntersecting && intersectionRatio >= 0.15);
        });
      }, { threshold: 0.15 })
      : null;
    cards.forEach((card) => beamObserver?.observe(card));
    cleanup.push(() => {
      beamObserver?.disconnect();
      cards.forEach((card) => card.classList.remove("is-card-beam-visible"));
    });

    if (collectionHeroImage?.complete && collectionHeroImage.naturalWidth === 0) collectionHeroImage.hidden = true;

    if (rail) createRailController(root, rail, {
      onIndexChange: (index, initiatedByUser) => {
        const itemCount = rail.querySelectorAll(":scope > [data-rail-item]").length;
        if (initiatedByUser && status && itemCount && index !== reportedIndex) {
          status.textContent = `Product ${index + 1} of ${itemCount}`;
        }
        reportedIndex = index;
      }
    }, cleanup);

    const stateFor = (card) => {
      let state = cardStates.get(card);
      if (!state) {
        state = { mediaIndex: 0, pending: false, disposed: false, controllers: new Set() };
        cardStates.set(card, state);
      }
      return state;
    };

    const mediaSlides = (card) => Array.from(card.querySelectorAll("[data-card-media-slide]"));

    const updateCardMedia = (card, requestedIndex) => {
      const state = stateFor(card);
      const media = card.querySelector("[data-card-media]");
      const previous = card.querySelector("[data-card-media-previous]");
      const next = card.querySelector("[data-card-media-next]");
      const link = card.querySelector(".homepage-product-card__image-link");
      const slides = mediaSlides(card);
      if (!media || !slides.length) return;

      slides.forEach((slide, index) => { slide.dataset.cardMediaIndex = String(index); });
      const index = Math.max(0, Math.min(requestedIndex, slides.length - 1));
      state.mediaIndex = index;
      media.dataset.cardMediaActive = String(index);
      media.dataset.cardMediaCount = String(slides.length);
      slides.forEach((slide, slideIndex) => {
        slide.setAttribute("aria-hidden", String(slideIndex !== index));
      });

      const previousDisabled = slides.length < 2 || index === 0;
      const nextDisabled = slides.length < 2 || index === slides.length - 1;
      const focusedControl = document.activeElement;
      if (slides.length < 2 && (focusedControl === previous || focusedControl === next)) {
        link?.focus({ preventScroll: true });
      } else if (focusedControl === previous && previousDisabled) {
        setControlState(next, nextDisabled);
        (nextDisabled ? link : next)?.focus({ preventScroll: true });
      } else if (focusedControl === next && nextDisabled) {
        setControlState(previous, previousDisabled);
        (previousDisabled ? link : previous)?.focus({ preventScroll: true });
      }
      setControlState(previous, previousDisabled);
      setControlState(next, nextDisabled);
      if (previous) previous.hidden = slides.length < 2;
      if (next) next.hidden = slides.length < 2;
    };

    const setCardStatus = (card, message) => {
      const liveRegion = card.querySelector("[data-card-status]");
      if (liveRegion) liveRegion.textContent = message;
    };

    const focusBeforeDisablingSize = (card, button) => {
      if (document.activeElement !== button) return;
      const buttons = Array.from(card.querySelectorAll("[data-card-size]"));
      const currentIndex = buttons.indexOf(button);
      const available = buttons.filter((candidate) => candidate !== button && !candidate.disabled);
      const destination = available.find((candidate) => buttons.indexOf(candidate) > currentIndex)
        || available[available.length - 1]
        || card.querySelector(".homepage-product-card__title");
      destination?.focus({ preventScroll: true });
    };

    const markSizeUnavailable = (card, button) => {
      focusBeforeDisablingSize(card, button);
      button.disabled = true;
      button.tabIndex = -1;
      button.classList.add("is-sold-out");
      button.setAttribute("aria-disabled", "true");
      button.setAttribute("aria-label", `Size ${button.dataset.cardSizeLabel || button.textContent.trim()}, sold out`);
    };

    const addSizeToCart = async (card, button) => {
      const state = stateFor(card);
      if (state.pending || state.disposed || button.disabled || button.getAttribute("aria-disabled") === "true") return;
      const addUrl = internalPath(card.dataset.cardCartAddUrl);
      const cartUrl = internalPath(card.dataset.cardCartUrl);
      const variantId = button.dataset.cardVariantId || "";
      const sizeLabel = button.dataset.cardSizeLabel || button.textContent.trim();
      const productTitle = card.dataset.cardProductTitle || "Product";
      const submittedLabel = `${productTitle}, ${sizeLabel}`;
      if (!addUrl || !cartUrl || !idLike(variantId)) return;

      state.pending = true;
      button.setAttribute("aria-disabled", "true");
      button.setAttribute("aria-busy", "true");
      setCardStatus(card, `Adding ${submittedLabel} to bag.`);

      const addController = new AbortController();
      state.controllers.add(addController);
      let addPayload = null;
      let inventoryFailure = false;
      try {
        const formData = new FormData();
        formData.set("id", variantId);
        formData.set("quantity", "1");
        let response;
        let result;
        try {
          ({ response, result } = await withTimeout(async () => {
            const addResponse = await fetch(addUrl, {
              method: "POST",
              headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
              body: formData,
              signal: addController.signal
            });
            return { response: addResponse, result: await parseJsonResponse(addResponse) };
          }, addController, productCardAddTimeout, "card-add-timeout"));
        } catch (error) {
          if (state.disposed || addController.signal.aborted && error.code !== "card-add-timeout") return;
          setCardStatus(card, `${submittedLabel}: ${productCardAmbiguousMessage}`);
          return;
        } finally {
          state.controllers.delete(addController);
        }

        const shopifyMessage = safeShopifyMessage(result.payload);
        if (!response.ok) {
          inventoryFailure = (response.status === 409 || response.status === 422)
            && productCardInventoryFailure.test(shopifyMessage);
          if (inventoryFailure) {
            markSizeUnavailable(card, button);
            setCardStatus(card, `${submittedLabel}: This size just sold out.`);
          } else {
            setCardStatus(card, `${submittedLabel}: ${shopifyMessage || "Unable to add this size. Try again."}`);
          }
          return;
        }
        if (!result.parsed || !validCartAddPayload(result.payload)) {
          setCardStatus(card, `${submittedLabel}: ${productCardAmbiguousMessage}`);
          return;
        }
        addPayload = result.payload;

        const refreshToken = cartCoordinator.reserveRefresh();
        const cartController = new AbortController();
        state.controllers.add(cartController);
        let cart = null;
        try {
          cart = await withTimeout(async () => {
            const cartResponse = await fetch(cartUrl, {
              headers: { Accept: "application/json" },
              signal: cartController.signal
            });
            if (!cartResponse.ok) return null;
            const cartResult = await parseJsonResponse(cartResponse);
            const itemCount = isRecord(cartResult.payload) ? Number(cartResult.payload.item_count) : NaN;
            return cartResult.parsed && Number.isFinite(itemCount) ? cartResult.payload : null;
          }, cartController, productCardCartTimeout, "card-cart-timeout");
        } catch (_) {
          if (state.disposed) return;
        } finally {
          state.controllers.delete(cartController);
        }

        if (state.disposed) return;
        document.dispatchEvent(new CustomEvent("jsca:cart:updated", {
          detail: { cart: cartCoordinator.isLatestRefresh(refreshToken) ? cart : null, item: addPayload }
        }));
        setCardStatus(card, `Added ${submittedLabel} to bag`);
      } finally {
        state.pending = false;
        button.removeAttribute("aria-busy");
        if (!inventoryFailure && !button.disabled) button.setAttribute("aria-disabled", "false");
      }
    };

    cards.forEach((card) => {
      stateFor(card);
      card.classList.add("is-product-card-enhanced");
      const sizeTray = card.querySelector("[data-card-size-tray]");
      if (sizeTray && sizeTray.querySelector("[data-card-size]")) sizeTray.hidden = false;
      else card.removeAttribute("data-card-direct-size");
      updateCardMedia(card, 0);
    });

    const cardUsesHoverSizes = () => window.matchMedia("(hover: hover) and (pointer: fine)").matches
      && window.matchMedia("(min-width: 769px)").matches;

    const closeSizeTrays = (exceptCard) => {
      root.querySelectorAll(".homepage-product-card.is-size-tray-open").forEach((openCard) => {
        if (openCard !== exceptCard) openCard.classList.remove("is-size-tray-open");
      });
    };

    addListener(root, "click", (event) => {
      const mediaControl = event.target.closest?.("[data-card-media-control]");
      if (mediaControl && root.contains(mediaControl)) {
        const card = mediaControl.closest(".homepage-product-card");
        if (!card || mediaControl.disabled || mediaControl.getAttribute("aria-disabled") === "true") return;
        event.preventDefault();
        event.stopPropagation();
        const direction = mediaControl.matches("[data-card-media-next]") ? 1 : -1;
        updateCardMedia(card, stateFor(card).mediaIndex + direction);
        return;
      }

      const sizeButton = event.target.closest?.("[data-card-size]");
      if (sizeButton && root.contains(sizeButton)) {
        event.preventDefault();
        event.stopPropagation();
        const card = sizeButton.closest(".homepage-product-card");
        if (card) void addSizeToCart(card, sizeButton);
        return;
      }

      const quickAdd = event.target.closest?.("[data-card-compact-quick-view]");
      if (quickAdd && root.contains(quickAdd) && !cardUsesHoverSizes()) {
        const card = quickAdd.closest(".homepage-product-card");
        const sizeTray = card?.querySelector("[data-card-size-tray]");
        if (card?.hasAttribute("data-card-direct-size") && sizeTray?.querySelector("[data-card-size]")) {
          if (!card.classList.contains("is-size-tray-open")) {
            event.preventDefault();
            event.stopPropagation();
            closeSizeTrays(card);
            card.classList.add("is-size-tray-open");
            const firstSize = sizeTray.querySelector("[data-card-size]:not([disabled])") || sizeTray.querySelector("[data-card-size]");
            firstSize?.focus({ preventScroll: true });
            setCardStatus(card, "Choose a size to add.");
          }
        }
      }
    }, undefined, cleanup);

    addListener(document, "pointerdown", (event) => {
      if (event.target.closest?.(".homepage-product-card.is-size-tray-open")) return;
      closeSizeTrays();
    }, undefined, cleanup);

    addListener(root, "error", (event) => {
      const image = event.target;
      if (!(image instanceof HTMLImageElement)) return;
      if (image.matches(".jsca-collection__hero-image")) {
        image.hidden = true;
        return;
      }
      if (!image.matches(".homepage-product-card__image")) return;
      const card = image.closest(".homepage-product-card");
      const slide = image.closest("[data-card-media-slide]");
      if (!card || !slide) return;
      const slides = mediaSlides(card);
      const failedIndex = slides.indexOf(slide);
      if (failedIndex === 0) {
        image.hidden = true;
        if (slide.querySelector(".homepage-product-card__image-placeholder")) return;
        const placeholder = document.createElement("span");
        placeholder.className = "homepage-product-card__image-placeholder";
        placeholder.textContent = image.alt || "Product image unavailable";
        slide.append(placeholder);
        return;
      }

      const state = stateFor(card);
      const nextIndex = failedIndex < state.mediaIndex
        ? state.mediaIndex - 1
        : Math.min(state.mediaIndex, slides.length - 2);
      slide.remove();
      updateCardMedia(card, nextIndex);
    }, true, cleanup);

    cleanup.push(() => {
      cards.forEach((card) => {
        const state = stateFor(card);
        state.disposed = true;
        state.controllers.forEach((controller) => controller.abort());
        state.controllers.clear();
        card.classList.remove("is-product-card-enhanced");
      });
    });
  };

  const initShopTheLook = (root, cleanup) => {
    const rail = root.querySelector("[data-look-rail]");
    if (!rail) return;

    const slides = Array.from(rail.querySelectorAll("[data-look-slide]"));
    const liveRegion = root.querySelector("[data-look-live]");
    const dialog = root.querySelector("[data-look-dialog]");
    const dialogContent = dialog?.querySelector("[data-look-dialog-content]");
    const dialogClose = dialog?.querySelector("[data-look-dialog-close]");
    const desktop = window.matchMedia("(min-width: 1024px)");
    const popoverEnabled = root.dataset.mobileProductPopover === "true";
    const canUseMobilePopover = popoverEnabled && dialog && dialogContent && typeof dialog.showModal === "function";
    let activeLook = 0;
    let lastTrigger = null;

    root.classList.add("is-enhanced");
    root.classList.toggle("has-mobile-popover", Boolean(canUseMobilePopover));

    const productCards = (slide) => Array.from(slide.querySelectorAll("[data-look-product]"));
    const hotspots = (slide) => Array.from(slide.querySelectorAll("[data-look-hotspot]"));
    const usesProductFallback = (slide) => slide.classList.contains("is-product-fallback") || (!desktop.matches && !canUseMobilePopover);

    const configureProductLinks = (slide, selectedIndex) => {
      const exposeAllProducts = usesProductFallback(slide);
      productCards(slide).forEach((card, index) => {
        const selected = index === selectedIndex;
        card.hidden = exposeAllProducts ? false : !selected;
        card.querySelectorAll("[data-look-product-link]").forEach((link) => {
          const reachable = slide === slides[activeLook] && (exposeAllProducts || desktop.matches && selected);
          link.tabIndex = reachable ? 0 : -1;
        });
      });
    };

    const updateLookAccessibility = (index) => {
      activeLook = index;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === activeLook;
        if (active) slide.setAttribute("aria-current", "true");
        else slide.removeAttribute("aria-current");
        hotspots(slide).forEach((hotspot) => {
          hotspot.tabIndex = active && !hotspot.hidden ? 0 : -1;
        });
        const selected = Math.max(0, hotspots(slide).findIndex((hotspot) => hotspot.getAttribute("aria-pressed") === "true"));
        configureProductLinks(slide, selected);
      });
    };

    const openProductDialog = (card, trigger) => {
      if (!canUseMobilePopover || desktop.matches) return;
      lastTrigger = trigger;
      const clone = card.cloneNode(true);
      clone.hidden = false;
      clone.querySelectorAll("a").forEach((link) => { link.tabIndex = 0; });
      dialogContent.replaceChildren(clone);
      if (!dialog.open) dialog.showModal();
      dialogClose?.focus();
    };

    const selectProduct = (slide, index, trigger, openDialog) => {
      const currentHotspots = hotspots(slide);
      const cards = productCards(slide);
      const boundedIndex = Math.max(0, Math.min(index, Math.max(0, cards.length - 1)));
      currentHotspots.forEach((hotspot, hotspotIndex) => {
        hotspot.setAttribute("aria-pressed", String(hotspotIndex === boundedIndex));
      });
      configureProductLinks(slide, boundedIndex);
      if (trigger?.dataset.liveText && liveRegion) liveRegion.textContent = trigger.dataset.liveText;
      if (openDialog && cards[boundedIndex]) openProductDialog(cards[boundedIndex], trigger);
    };

    slides.forEach((slide) => {
      const currentHotspots = hotspots(slide);
      selectProduct(slide, 0, null, false);
      currentHotspots.forEach((hotspot) => {
        addListener(hotspot, "click", () => {
          const index = Number.parseInt(hotspot.dataset.productIndex || "0", 10);
          selectProduct(slide, index, hotspot, true);
        }, undefined, cleanup);
      });

      slide.querySelectorAll("[data-look-image]").forEach((image) => {
        addListener(image, "error", () => {
          image.hidden = true;
          slide.classList.add("is-product-fallback");
          slide.querySelector("[data-look-placeholder]")?.removeAttribute("hidden");
          hotspots(slide).forEach((hotspot) => { hotspot.hidden = true; });
          updateLookAccessibility(activeLook);
        }, undefined, cleanup);
      });
    });

    const controller = createRailController(root, rail, {
      centered: true,
      onIndexChange: updateLookAccessibility
    }, cleanup);

    addListener(root, "keydown", (event) => {
      const focusedHotspot = event.target.closest?.("[data-look-hotspot]");
      if (!focusedHotspot) return;
      const currentSlide = focusedHotspot.closest("[data-look-slide]");
      const currentHotspots = currentSlide ? hotspots(currentSlide).filter((hotspot) => !hotspot.hidden) : [];
      const currentIndex = currentHotspots.indexOf(focusedHotspot);
      if (currentIndex < 0) return;

      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        const nextIndex = event.key === "ArrowDown"
          ? Math.min(currentHotspots.length - 1, currentIndex + 1)
          : Math.max(0, currentIndex - 1);
        currentHotspots[nextIndex].focus();
        selectProduct(currentSlide, nextIndex, currentHotspots[nextIndex], false);
      } else if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        const slideIndex = slides.indexOf(currentSlide);
        const destinationIndex = event.key === "ArrowRight"
          ? Math.min(slides.length - 1, slideIndex + 1)
          : Math.max(0, slideIndex - 1);
        if (destinationIndex === slideIndex) return;
        controller.scrollToIndex(destinationIndex);
        const destinationHotspots = hotspots(slides[destinationIndex]).filter((hotspot) => !hotspot.hidden);
        const destinationHotspot = destinationHotspots[Math.min(currentIndex, destinationHotspots.length - 1)];
        if (destinationHotspot) {
          window.requestAnimationFrame(() => {
            destinationHotspot.focus();
            selectProduct(slides[destinationIndex], Number.parseInt(destinationHotspot.dataset.productIndex || "0", 10), destinationHotspot, false);
          });
        } else {
          const destinationLink = slides[destinationIndex].querySelector("[data-look-product-link]");
          window.requestAnimationFrame(() => (destinationLink || rail).focus());
        }
      } else {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    }, undefined, cleanup);

    addListener(dialogClose, "click", () => dialog?.close(), undefined, cleanup);
    addListener(dialog, "close", () => {
      if (lastTrigger?.isConnected) lastTrigger.focus();
      lastTrigger = null;
    }, undefined, cleanup);

    const layoutChange = () => {
      if (desktop.matches && dialog?.open) dialog.close();
      updateLookAccessibility(controller.nearestIndex());
      controller.update();
    };
    desktop.addEventListener("change", layoutChange);
    cleanup.push(() => desktop.removeEventListener("change", layoutChange));

    updateLookAccessibility(0);
    cleanup.push(() => {
      if (dialog?.open) dialog.close();
      root.classList.remove("is-enhanced");
      root.classList.remove("has-mobile-popover");
      slides.forEach((slide) => {
        slide.removeAttribute("aria-current");
        productCards(slide).forEach((card) => {
          card.hidden = false;
          card.querySelectorAll("[data-look-product-link]").forEach((link) => link.removeAttribute("tabindex"));
        });
        hotspots(slide).forEach((hotspot) => hotspot.removeAttribute("tabindex"));
      });
    });
  };

  const initialize = (root) => {
    if (instances.has(root)) return;
    const cleanup = [];
    instances.set(root, cleanup);
    if (root.matches("[data-editorial-section]")) initEditorial(root, cleanup);
    else if (root.matches("[data-product-carousel]")) initProductCarousel(root, cleanup);
    else if (root.matches("[data-shop-the-look]")) initShopTheLook(root, cleanup);
  };

  const destroy = (root) => {
    const cleanup = instances.get(root);
    if (!cleanup) return;
    cleanup.reverse().forEach((dispose) => dispose());
    instances.delete(root);
  };

  const rootsWithin = (container) => {
    const selector = "[data-editorial-section], [data-product-carousel], [data-shop-the-look]";
    const roots = [];
    if (container instanceof Element && container.matches(selector)) roots.push(container);
    if (container.querySelectorAll) roots.push(...container.querySelectorAll(selector));
    return roots;
  };

  rootsWithin(document).forEach(initialize);
  document.addEventListener("shopify:section:load", (event) => rootsWithin(event.target).forEach(initialize));
  document.addEventListener("shopify:section:unload", (event) => rootsWithin(event.target).forEach(destroy));
})();
